// Component to display the basic Job info as a card header
import { getJobStartDatetime } from '@isd/lib/firebase/db/helpers';
import { JobDoc } from '@isd/lib/firebase/db/metaTypes';

import { replaceParamsInRoute } from '@isd/lib/helpers/functions';

import { useEditSchedulerContext } from '@/context/EditSchedulerContext';
import { useSchedulerContext } from '@/context/SchedulerContext';

import JobBrokerCompanyName from '@/components/Scheduler/SchedulerMain/JobCard/JobInfo/JobBrokerCompanyName';
import JobOperatorsCount from '@/components/Scheduler/SchedulerMain/JobCard/JobInfo/JobOperatorsCount';
import JobProductLabel from '@/components/Scheduler/SchedulerMain/JobCard/JobInfo/JobProductLabel';
import JobStatusBadge from '@/components/Scheduler/SchedulerMain/JobCard/JobInfo/JobStatusBadge';
import ExternalLinkText from '@/components/common/ExternalLinkText';
import PinToggle from '@/components/common/PinToggle';

import {
  reactAppJobDetailsRoute,
  reactAppJobEditRoute,
  reactAppUrl,
} from '@/env';

export default function JobInfo({ jobDoc }: { jobDoc: JobDoc }) {
  const jobData = jobDoc.data();

  const shouldOpenEditJobForm = jobDoc.get('overallStatus') === 'INCOMPLETE';

  const onClickJobTitle = () => {
    const jobId = jobDoc.id;
    const jobDetailsUrl = `${reactAppUrl}${replaceParamsInRoute(reactAppJobDetailsRoute, { jobId })}`;
    const editJobUrl = `${reactAppUrl}${replaceParamsInRoute(reactAppJobEditRoute, { jobId })}`;
    window.open(shouldOpenEditJobForm ? editJobUrl : jobDetailsUrl);
  };

  return (
    <div className="job-info grid h-full grid-cols-6">
      <div className="col-span-4">
        <JobStatusBadge jobDoc={jobDoc} />
        <ExternalLinkText
          text={jobData?.name || ''}
          textClassName="job-title h-12"
          onClick={onClickJobTitle}
          underlineOnHover
        />
      </div>
      <div className="col-span-2 flex items-start justify-end gap-1">
        <span className="job-start-time line-clamp-1 text-right">
          {getJobStartDatetime(jobDoc).getTimeStr()}
        </span>
        <JobPinToggle jobDoc={jobDoc} />
      </div>
      <div className="col-span-6">
        <JobProductLabel jobDoc={jobDoc} />
      </div>
      <div className="col-span-4 mt-2">
        <div className="job-client-name font-bold" title="Job Client">
          {jobData?.clientName}
        </div>
        <JobBrokerCompanyName jobDoc={jobDoc} />
      </div>
      <JobOperatorsCount classNames="col-span-2" jobDoc={jobDoc} />
    </div>
  );
}

/**
 * Pin toggle for a job card. Pinned jobs are the only ones the List shows and
 * the ones the Map focuses on. Disabled while job assignments are being edited,
 * so the job being edited can't be hidden.
 * @param {object} props
 * @param {JobDoc} props.jobDoc
 * @returns {React.JSX.Element}
 */
function JobPinToggle({ jobDoc }: { jobDoc: JobDoc }) {
  const { pinnedJobIds, togglePinnedJob } = useSchedulerContext();
  const { selectedJob, operationsRecordList } = useEditSchedulerContext();
  const isEditing = !!selectedJob || !!operationsRecordList.length;
  return (
    <PinToggle
      isPinned={pinnedJobIds.includes(jobDoc.id)}
      onToggle={() => togglePinnedJob(jobDoc.id)}
      label={jobDoc.get('name') || 'job'}
      disabled={isEditing}
      className="pin-job-button -mt-0.5"
    />
  );
}
